/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {ReactNode} from 'react';
import Button, {ButtonProps} from '@material-ui/core/Button';
import {makeStyles} from '@material-ui/core/styles';
import {dlsColor, dlsTextColor} from '@ion/dls/src/theme/theme';
import {DLSTheme} from '../../../types/types';
import {StableButtonLabel} from './StableButtonLabel';

interface LinkedTextButtonProps extends ButtonProps {
  children: ReactNode;
  disableStableLabel?: boolean;
};

const useStyles = makeStyles((theme: DLSTheme) => ({
  container : {
    padding : 0
  },
  root : {
    color                 : dlsTextColor('blue'),
    height                : '24px',
    display               : 'inline-flex',
    padding               : '0 8px',
    fontSize              : '14px',
    fontFamily            : 'Roboto',
    fontWeight            : 500,
    lineHeight            : '16px',
    flexDirection         : 'column',
    letterSpacing         : '0.5px',
    '&:hover, &$selected' : {
      textDecoration  : 'underline',
      backgroundColor : 'transparent'
    },
    '&:focus' : {
      outline : 'none'
    },
    '&:focus-visible' : {
      outline       : `2px solid ${dlsColor('skyBlue')}`,
      borderRadius  : '4px',
      outlineOffset : '2px'
    },
    '&$disabled' : {
      color          : dlsColor('grey4'),
      cursor         : 'none',
      textDecoration : 'none'
    }
  }
}));

/**
 * LinkedTextButton is a text-based button designed to look and behave like a hyperlink 
 * within the DLS system. It is best used for navigation, "Show More" actions, 
 * or secondary commands that should not carry the visual weight of a standard button.
 *
 * ```typescript
 *
 * import React from 'react';
 * import {LinkedTextButton} from '@ion/dls/src/components/buttons/LinkedTextButton';
 *
 * function MyComponent() {
 *   return (
 *     <React.Fragment>
 *       <LinkedTextButton>Clickable Link</LinkedTextButton>
 *       <LinkedTextButton onClick={() => console.log('clicked')}>
 *       </LinkedTextButton>
 *       <br />
 *       <LinkedTextButton disabled>Disabled Link</LinkedTextButton>
 *     </React.Fragment>
 *   );
 * }
 *
 * ```
 * 
 */
export const LinkedTextButton = React.forwardRef<HTMLButtonElement, LinkedTextButtonProps>(({
  disableStableLabel = false,
  ...props
}, ref) => {
  const classes = useStyles();

  return (
    <div className={classes.container}>
      <Button disableRipple disableFocusRipple
        ref={ref}
        classes={{
          root : classes.root
        }}
        {...props}>
        {disableStableLabel ? props.children : <StableButtonLabel>{props.children}</StableButtonLabel>}
      </Button>
    </div>
  );
});

LinkedTextButton.displayName = 'LinkedTextButton';

export default LinkedTextButton;
